type SocketLike = {
    emit: (...args: any[]) => void
    connected?: boolean
    connect?: () => void
}

type WaitForSocketOptions = {
    timeoutMs: number
    pollIntervalMs?: number
}

type EmitWithAckOptions<TPayload> = {
    getSocket: () => SocketLike | null | undefined
    event: string
    payload: TPayload
    waitTimeoutMs: number
    ackTimeoutMs: number
    pollIntervalMs?: number
}

export async function waitForSocket(
    getSocket: () => SocketLike | null | undefined,
    options: WaitForSocketOptions
): Promise<SocketLike> {
    const pollIntervalMs = options.pollIntervalMs ?? 100
    const startedAt = Date.now()

    while (true) {
        const socket = getSocket()
        if (socket) {
            // Socket.IO instantiates synchronously, but ack-based emits only work
            // once the transport handshake has completed.
            if (typeof socket.connected !== 'boolean' || socket.connected) {
                return socket
            }

            socket.connect?.()
        }

        if (Date.now() - startedAt >= options.timeoutMs) {
            throw new Error('Socket unavailable')
        }

        await new Promise(resolve => setTimeout(resolve, pollIntervalMs))
    }
}

export async function emitWithAck<TResponse, TPayload = Record<string, unknown>>(
    options: EmitWithAckOptions<TPayload>
): Promise<TResponse> {
    const socket = await waitForSocket(options.getSocket, {
        timeoutMs: options.waitTimeoutMs,
        pollIntervalMs: options.pollIntervalMs,
    })

    return await new Promise<TResponse>((resolve, reject) => {
        const timeout = setTimeout(() => {
            reject(new Error('Socket ack timed out'))
        }, options.ackTimeoutMs)

        socket.emit(options.event, options.payload, (result: TResponse) => {
            clearTimeout(timeout)
            resolve(result)
        })
    })
}
